import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { auditHead, type PageAudit } from "@/lib/seo-audit";
import { SEO_TARGETS } from "@/lib/seo-targets";
import {
  CHECK_KEYS,
  buildCsvReport,
  buildMarkdownReport,
  downloadFile,
  worstState,
} from "@/lib/seo-report";
import { cn } from "@/lib/utils";


export const Route = createFileRoute("/_authenticated/admin/seo")({
  component: SeoChecklistPage,
  head: () => ({
    meta: [
      { title: "SEO Checklist - KCN Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const CHECK_LABELS = CHECK_KEYS;

function Dot({ state }: { state: "pass" | "warn" | "fail" }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block h-2 w-2 rounded-full shrink-0",
        state === "pass" && "bg-success",
        state === "warn" && "bg-amber-500",
        state === "fail" && "bg-primary",
      )}
    />
  );
}

function SeoChecklistPage() {
  const [pages, setPages] = useState<PageAudit[] | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [origin, setOrigin] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  /**
   * Loads each route in an invisible same-origin frame and reads the browser's
   * completed head. Preview fetches can be intercepted and fail even when the
   * page itself loads, which previously turned one transport failure into many
   * false canonical, schema, and keyword errors.
   */
  function inspectPage(target: (typeof SEO_TARGETS)[number]): Promise<PageAudit> {
    return new Promise((resolve) => {
      const frame = document.createElement("iframe");
      frame.hidden = true;
      frame.setAttribute("aria-hidden", "true");
      let settled = false;

      const finish = (result: PageAudit) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        frame.remove();
        resolve(result);
      };

      const timeout = window.setTimeout(() => {
        finish(auditHead(target, "", 0, "Page inspection timed out"));
      }, 15000);

      frame.onload = () => {
        try {
          const head = frame.contentDocument?.head?.outerHTML;
          if (!head) {
            finish(auditHead(target, "", 0, "Rendered head was unavailable"));
            return;
          }
          finish(auditHead(target, head, 200));
        } catch (inspectionError) {
          finish(auditHead(target, "", 0, (inspectionError as Error).message));
        }
      };
      frame.onerror = () => finish(auditHead(target, "", 0, "Page failed to load"));
      frame.src = `${target.path}${target.path.includes("?") ? "&" : "?"}seo-check=${Date.now()}`;
      document.body.appendChild(frame);
    });
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const base = window.location.origin;
      const results: PageAudit[] = [];
      const batchSize = 6;
      for (let i = 0; i < SEO_TARGETS.length; i += batchSize) {
        const batch = await Promise.all(
          SEO_TARGETS.slice(i, i + batchSize).map(inspectPage),
        );
        results.push(...batch);
      }
      const res = { pages: results, checkedAt: new Date().toISOString(), origin: base };
      setPages(res.pages);
      setCheckedAt(res.checkedAt);
      setOrigin(res.origin);
      return res;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setLoading(false);
    }
  }


  /** One click: scan every tracked page, then download the full QA report. */
  async function runReport() {
    const res = await refresh();
    if (!res) return;
    downloadFile(
      `seo-qa-${new Date().toISOString().slice(0, 10)}.md`,
      buildMarkdownReport(res.pages, { origin: res.origin, checkedAt: res.checkedAt }),
      "text/markdown",
    );
  }


  const totals = pages
    ? pages.reduce(
        (acc, p) => {
          for (const { key } of CHECK_LABELS) acc[p.checks[key].state] += 1;
          return acc;
        },
        { pass: 0, warn: 0, fail: 0 } as Record<"pass" | "warn" | "fail", number>,
      )
    : null;

  const groups = pages
    ? Array.from(new Set(pages.map((p) => p.group))).map((g) => ({
        group: g,
        rows: pages.filter((p) => p.group === g),
      }))
    : [];

  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 mb-8 sm:flex sm:justify-between sm:items-center">
        <div className="min-w-0">
          <h1 className="headline-md">SEO checklist</h1>
          <p className="text-foreground/65 text-sm mt-2 max-w-2xl">
            Fetches every tracked page from this deployment and inspects the rendered head:
            title, meta description, canonical, OG tags, FAQPage schema, and whether the
            keyword targets appear in the title or description.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="shrink-0 bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground disabled:opacity-60"
          >
            {loading ? "Scanning…" : pages ? "Re-run" : "Run checklist"}
          </button>
          <button
            onClick={runReport}
            disabled={loading}
            className="shrink-0 border border-border px-6 py-3 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
          >
            {loading ? "Scanning…" : "One-click QA report"}
          </button>
          {pages ? (
            <button
              onClick={() =>
                downloadFile(
                  `seo-qa-${new Date().toISOString().slice(0, 10)}.csv`,
                  buildCsvReport(pages),
                  "text/csv",
                )
              }
              className="shrink-0 border border-border px-6 py-3 text-xs uppercase tracking-[0.18em]"
            >
              CSV
            </button>
          ) : null}
        </div>
      </div>


      {error ? (
        <p className="border border-primary/40 bg-primary/5 p-4 text-sm text-foreground/80 mb-6">
          {error}
        </p>
      ) : null}

      {totals ? (
        <div className="flex flex-wrap items-center gap-6 border-y border-border py-4 mb-8 text-sm">
          <span className="flex items-center gap-2"><Dot state="pass" /> {totals.pass} passing</span>
          <span className="flex items-center gap-2"><Dot state="warn" /> {totals.warn} warnings</span>
          <span className="flex items-center gap-2"><Dot state="fail" /> {totals.fail} failing</span>
          <span className="text-foreground/55">
            {origin} · {checkedAt ? new Date(checkedAt).toLocaleString() : null}
          </span>
        </div>
      ) : null}

      {pages ? (
        <div className="grid gap-6 md:grid-cols-3 mb-10">
          {(
            [
              { title: "Canonical issues", keys: ["canonical"] as const },
              { title: "Schema issues", keys: ["faq", "breadcrumb"] as const },
              { title: "Keyword mismatches", keys: ["keyword"] as const },
            ]
          ).map(({ title, keys }) => {
            const rows = pages.flatMap((p) =>
              keys
                .filter((k) => p.checks[k].state !== "pass")
                .map((k) => ({ page: p, note: p.checks[k].note, state: p.checks[k].state })),
            );
            return (
              <div key={title} className="border border-border p-4">
                <h3 className="text-xs uppercase tracking-[0.18em] text-foreground/55 mb-3">
                  {title} ({rows.length})
                </h3>
                {rows.length === 0 ? (
                  <p className="text-sm text-foreground/60">All clear.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {rows.map((r, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5"><Dot state={r.state} /></span>
                        <span>
                          <span className="block">{r.page.label}</span>
                          <span className="block text-xs text-foreground/60">{r.note}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {!pages && !loading ? (
        <p className="text-foreground/60 text-sm">
          Run the checklist to audit all tracked pages. Targets live in
          <code className="mx-1">src/lib/seo-targets.ts</code> and mirror the keyword targets doc.
        </p>
      ) : null}

      {groups.map(({ group, rows }) => (
        <section key={group} className="mb-10">
          <h2 className="text-xs uppercase tracking-[0.22em] text-foreground/55 mb-3">{group}</h2>
          <div className="border border-border divide-y divide-border">
            {rows.map((p) => {
              const worst = worstState(p);
              const isOpen = open === p.path;
              return (
                <div key={p.path}>
                  <button
                    onClick={() => setOpen(isOpen ? null : p.path)}
                    className="w-full text-left px-4 py-3 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 hover:bg-foreground/[0.03]"
                  >
                    <Dot state={worst} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{p.label}</span>
                      <span className="block truncate text-xs text-foreground/55">{p.path}</span>
                    </span>
                    <span className="hidden sm:flex items-center gap-3 text-[0.65rem] uppercase tracking-[0.14em] text-foreground/55">
                      {CHECK_LABELS.map((c) => (
                        <span key={c.key} className="flex items-center gap-1.5">
                          <Dot state={p.checks[c.key].state} />
                          {c.label}
                        </span>
                      ))}
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="px-4 pb-5 pt-1 space-y-4 text-sm">
                      <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                        {CHECK_LABELS.map((c) => (
                          <li key={c.key} className="flex items-start gap-2">
                            <span className="mt-1.5"><Dot state={p.checks[c.key].state} /></span>
                            <span>
                              <strong className="font-medium">{c.label}:</strong>{" "}
                              <span className="text-foreground/70">{p.checks[c.key].note}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                      <dl className="grid sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-6 gap-y-2 text-foreground/70 border-t border-border pt-4">
                        <dt className="text-foreground/55">Title</dt>
                        <dd className="break-words">{p.title ?? "-"}</dd>
                        <dt className="text-foreground/55">Description</dt>
                        <dd className="break-words">{p.description ?? "-"}</dd>
                        <dt className="text-foreground/55">Canonical</dt>
                        <dd className="break-all">{p.canonical ?? "-"}</dd>
                        <dt className="text-foreground/55">Schema</dt>
                        <dd>{p.schemaTypes.length ? p.schemaTypes.join(", ") : "none"}</dd>
                        <dt className="text-foreground/55">Primary keyword</dt>
                        <dd>{p.primary}</dd>
                        <dt className="text-foreground/55">Secondary</dt>
                        <dd>{p.secondary.length ? p.secondary.join(" · ") : "-"}</dd>
                      </dl>
                      <a
                        href={p.path}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block text-xs uppercase tracking-[0.18em] text-primary"
                      >
                        Open page
                      </a>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
